import { Controller } from "@hotwired/stimulus"
import { apiGet, apiPost, apiDelete, formatMoney, t } from "../api_helper"
import { icon } from "../lib/icons"
import { networkStatus } from "../lib/network_status"
import { isOfflineError, showOfflinePlaceholder, alertOffline } from "../lib/offline_ui"

export default class extends Controller {
  static targets = ["list", "form", "addBtn", "amount", "categorySelect", "periodSelect"]

  connect() {
    this.load()
  }

  async load() {
    try {
      const [budgetData, catData] = await Promise.all([
        apiGet("/api/budgets"),
        apiGet("/api/categories")
      ])
      const budgets = budgetData.budgets || budgetData
      this.currency = budgetData.currency || "RUB"
      this.categories = catData.categories || catData
      this.renderList(budgets)
      this.renderCategoryOptions(budgets)
    } catch (e) {
      if (isOfflineError(e)) { showOfflinePlaceholder(this.hasListTarget ? this.listTarget : null, () => this.load()); return }
      console.error("Budgets load error:", e)
    }
  }

  renderCategoryOptions(budgets) {
    if (!this.hasCategorySelectTarget || !this.categories) return

    const usedCategoryIds = new Set(budgets.filter(b => b.category).map(b => b.category.id))
    const expenseCategories = this.categories.filter(c => c.kind === "expense" && !usedCategoryIds.has(c.id))

    let html = `<option value="">${t("budgets.total_budget")}</option>`
    html += expenseCategories.map(c =>
      `<option value="${c.id}">${c.icon} ${c.name}</option>`
    ).join("")

    this.categorySelectTarget.innerHTML = html
  }

  renderList(budgets) {
    if (!this.hasListTarget) return

    if (!budgets.length) {
      this.listTarget.innerHTML = `
        <div style="text-align: center; padding: 2rem 1rem;">
          <div style="margin-bottom: 0.5rem;">${icon("chart-bar", "w-10 h-10 text-slate-300")}</div>
          <div style="color: rgb(148,163,184); font-size: 0.875rem;">${t("budgets.no_budgets")}</div>
        </div>`
      return
    }

    this.listTarget.innerHTML = `<div style="display: flex; flex-direction: column; gap: 0.75rem;">
      ${budgets.map(b => {
        const pct = Math.min(b.percentage_used, 100)
        const categoryName = b.category ? `${b.category.icon} ${b.category.name}` : t("budgets.total_budget")

        let barColor
        if (b.exceeded) {
          barColor = "rgb(239,68,68)"
        } else if (pct > 80) {
          barColor = "#F59E0B"
        } else {
          barColor = "rgb(16,185,129)"
        }

        return `
          <div style="padding: 1rem; border-radius: 0.75rem; background-color: rgb(248,250,252);">
            <div style="display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 0.25rem;">
              <div>
                <div style="font-weight: 600; font-size: 0.875rem;">${categoryName}</div>
                <div style="font-size: 0.75rem; margin-top: 0.125rem; color: rgb(148,163,184);">${t("budgets." + b.period) || b.period}</div>
              </div>
              <button data-action="click->pwa-budgets#deleteBudget" data-budget-id="${b.id}"
                      style="font-size: 0.75rem; padding: 0.25rem 0.5rem; border-radius: 0.5rem; color: rgb(148,163,184); background: none; border: none; cursor: pointer;">&times;</button>
            </div>
            <div style="display: flex; justify-content: space-between; align-items: baseline; margin-bottom: 0.5rem;">
              <span style="font-size: 1.125rem; font-weight: 700;">${formatMoney(b.spent, this.currency)}</span>
              <span style="font-size: 0.75rem; color: rgb(148,163,184);">/ ${formatMoney(b.amount, this.currency)}</span>
            </div>
            <div style="width: 100%; border-radius: 9999px; overflow: hidden; height: 6px; background-color: #e2e8f0;">
              <div style="width: ${pct}%; height: 100%; border-radius: 9999px; background-color: ${barColor}; transition: width 0.5s ease;"></div>
            </div>
            ${b.exceeded
              ? `<div style="font-size: 0.75rem; margin-top: 0.5rem; font-weight: 500; color: rgb(239,68,68);">${t("budgets.exceeded")}</div>`
              : `<div style="font-size: 0.75rem; margin-top: 0.375rem; text-align: right; color: rgb(148,163,184);">${Math.round(pct)}%</div>`
            }
          </div>`
      }).join("")}
    </div>`
  }

  showForm() {
    if (this.hasFormTarget) this.formTarget.classList.remove("hidden")
    if (this.hasAmountTarget) setTimeout(() => this.amountTarget.focus(), 100)
  }

  hideForm() {
    if (this.hasFormTarget) this.formTarget.classList.add("hidden")
    if (this.hasAmountTarget) this.amountTarget.value = ""
  }

  stopPropagation(event) {
    event.stopPropagation()
  }

  async submit(event) {
    event.preventDefault()
    if (!networkStatus.online) { alertOffline(); return }
    try {
      await apiPost("/api/budgets", {
        budget: {
          amount: parseFloat(this.amountTarget.value),
          category_id: this.categorySelectTarget.value || null,
          period: this.periodSelectTarget.value
        }
      })
      this.amountTarget.value = ""
      this.hideForm()
      this.load()
    } catch (e) {
      const msg = e.message || "Error"
      alert(msg)
    }
  }

  async deleteBudget(event) {
    if (!networkStatus.online) { alertOffline(); return }
    const id = event.currentTarget.dataset.budgetId
    if (!confirm(t("budgets.delete_confirm") || "Delete this budget?")) return
    try {
      await apiDelete(`/api/budgets/${id}`)
      this.load()
    } catch (e) {
      console.error("Budget delete error:", e)
    }
  }
}
